import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, CheckSquare, Square } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { store } from '../store';
import { setUserProfile, completeRegistration, setSelfie, signupVisitor, fetchVisitorData, clearApiError } from '../store/slices/userSlice';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';
import { getCountryCodeForAPI, AgeGroupValue, getAgeGroupLabel } from '../utils/constants';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import NationalityModal from '../components/NationalityModal';
import AgeGroupModal from '../components/AgeGroupModal';
import StatusModal from '../components/StatusModal';

export default function Registration() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userState = useAppSelector(state => state.user);
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    const [form, setForm] = useState({
        name: userState.name || '',
        email: userState.email || '',
        ageGroup: userState.ageGroup || '', // Store selected age group
        nationality: userState.nationality || '',
    });
    
    // Modal state for age group selection
    const [ageGroupModalOpen, setAgeGroupModalOpen] = useState(false);

    // Use the custom hook for scroll detection
    const isAtBottom = useScrollToBottom({
        containerSelector: '.main-content-wrapper'
    });
    
    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [alreadyRegisteredModalOpen, setAlreadyRegisteredModalOpen] = useState(false);

    
    useEffect(() => {
        // Check for existing selfie in localStorage
        const selfieData = localStorage.getItem('selfie');
        if (selfieData && !userState.selfieUrl) {
            dispatch(setSelfie(selfieData));
        }
    }, [dispatch, userState.selfieUrl]);

    // Clear API error when component unmounts or user starts typing
    useEffect(() => {
        return () => {
            dispatch(clearApiError());
        };
    }, [dispatch]);
    
    // Log Redux state changes for debugging and show appropriate modal
    useEffect(() => {
        if (userState.visitorId && !userState.isLoading) {
            console.log('🔍 Redux user state updated:', {
                visitorId: userState.visitorId,
                name: userState.name,
                email: userState.email,
                ageGroup: userState.ageGroup,
                nationality: userState.nationality,
                isAuthenticated: userState.isAuthenticated,
                hasCompletedRegistration: userState.hasCompletedRegistration,
                dataWasExpired: userState.dataWasExpired
            });
            
            // Determine if this is a fresh registration or returning user
            const isFreshRegistration = userState.hasCompletedRegistration && 
                                    userState.name && 
                                    userState.email && 
                                    userState.ageGroup && 
                                    userState.nationality;
            
            const isReturningUser = userState.visitorId && 
                                (!userState.name || !userState.email || !userState.ageGroup || !userState.nationality);
            
            if (isFreshRegistration && !isReturningUser) {
                // Fresh registration - show success modal
                setSuccessModalOpen(true);
            } else if (isReturningUser) {
                // Returning user with preserved visitorId - show "already registered" modal
                console.log('👤 Returning user detected, showing already registered modal');
                setAlreadyRegisteredModalOpen(true);
            }
        }
    }, [userState.visitorId, userState.isLoading, userState.hasCompletedRegistration, userState.name, userState.email, userState.ageGroup, userState.nationality, userState.dataWasExpired]);
    
    // Show error modal when API error occurs
    useEffect(() => {
        if (userState.apiError && !userState.isLoading) {
            setErrorModalOpen(true);
        }
    }, [userState.apiError, userState.isLoading]);

    // Show info modal during loading (onboarding process)
    useEffect(() => {
        if (userState.isLoading) {
            setInfoModalOpen(true);
        } else {
            setInfoModalOpen(false);
        }
    }, [userState.isLoading]);
    
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, value, type, checked } = e.target;
        
        // Clear API error when user starts typing
        if (userState.apiError) {
            dispatch(clearApiError());
        }
        
        if (name === 'privacyAccepted') {
            dispatch(setUserProfile({
                privacyAccepted: checked
            }));
            return;
        }
        
        // Age group is now handled by modal selection, not direct input
        if (name === 'ageGroup') {
            return; // Age group selection is handled by modal
        }
        
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }
    
    // Handle nationality selection
    function handleNationalitySelect(country: string) {
        setForm(prev => ({
            ...prev,
            nationality: country
        }));
        
        // Clear API error when user makes selection
        if (userState.apiError) {
            dispatch(clearApiError());
        }
    }
    
    // Handle age group selection
    function handleAgeGroupSelect(ageGroupValue: AgeGroupValue) {
        setForm(prev => ({
            ...prev,
            ageGroup: ageGroupValue
        }));
        
        // Clear API error when user makes selection
        if (userState.apiError) {
            dispatch(clearApiError());
        }
    }
    
    // Handle privacy policy toggle
    function handlePrivacyToggle() {
        if (userState.isLoading) return;
        
        dispatch(setUserProfile({
            privacyAccepted: !userState.privacyAccepted
        }));
        
        // Clear API error when user makes selection
        if (userState.apiError) {
            dispatch(clearApiError());
        }
    }
    
    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        
        if (!isFormValid) {
            return;
        }
        
        // Update user state in Redux first
        dispatch(setUserProfile({
            name: form.name,
            email: form.email,
            ageGroup: form.ageGroup as AgeGroupValue, // Store the age group value in Redux
            nationality: form.nationality,
        }));
        
        try {
            // Call API to signup visitor
            const countryCode = getCountryCodeForAPI(form.nationality);
            console.log(`🌍 Converting "${form.nationality}" to country code: "${countryCode}"`);
            
            const signupData = {
                name: form.name,
                email: form.email,
                ageGroup: form.ageGroup as AgeGroupValue,
                nationality: countryCode, // Convert to country code
                language: userState.language || 'en'
            };
            
            console.log('📤 Sending signup data:', signupData);
            
            const result = await dispatch(signupVisitor(signupData));
            
            if (signupVisitor.fulfilled.match(result)) {
                // Log the action result payload
                console.log('📦 Signup action result payload:', result.payload);
                
                // Mark registration as complete
                dispatch(completeRegistration());
            }
            // If the request failed, the error will be shown in the UI via userState.apiError
        } catch (error) {
            console.error('Unexpected error during signup:', error);
        }
    }
    
    function skipRegistration() {
        navigate('/selfie');
    }

    // Validate email
    const isEmailValid = useMemo(() => {
        return /^\S+@\S+\.\S+$/.test(form.email);
    }, [form.email]);
    
    // Validate form fields
    const isFormValid = useMemo(() => {
        return (
            form.name.trim() !== '' && 
            isEmailValid && 
            form.ageGroup.trim() !== '' && 
            form.nationality !== ''
        );
    }, [form.name, isEmailValid, form.ageGroup, form.nationality]);
    
    // Helper function to get country display text
    const getCountryDisplay = (country: string) => {
        if (country === "China") return "China (中国)";
        if (country === "Taiwan") return "Taiwan (台灣)";
        return country;
    };
    
    return (
        <div className={`
            main-content-wrapper !pb-[120px] bg-image-galaxy 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="mt-6 mb-4">
                <h1 className="main-content-title"
                    dangerouslySetInnerHTML={{ __html: t('registration.headline') }}
                />
            </TcPaper>
            
            <TcPaper classNameStyle="general-content mb-4">
                <p className="mb-2"
                    dangerouslySetInnerHTML={{ __html: t('registration.description') }}
                />
                


                {/* Info Modal - Onboarding Process */}
                <StatusModal
                    isOpen={infoModalOpen}
                    onClose={() => {}} // Prevent closing during loading
                    type="info"
                    title={t('registration.onboarding')}
                    message={t('registration.onboardingMessage')}
                    showActionButton={false}
                />

                {/* Success Modal */}
                <StatusModal
                    isOpen={successModalOpen}
                    onClose={() => setSuccessModalOpen(false)}
                    type="success"
                    visitorId={userState.visitorId}
                    showVisitorId={true}
                    title={t('common.success')}
                    message={t('registration.success')}
                    actionButtonText={t('common.letsGo')}
                    onAction={() => navigate('/selfie')}
                />

                {/* Error Modal */}
                <StatusModal
                    isOpen={errorModalOpen}
                    onClose={() => {
                        setErrorModalOpen(false);
                        dispatch(clearApiError());
                    }}
                    type="error"
                    title={t('common.error')}
                    message={userState.apiError || t('common.errorOccurred')}
                    actionButtonText={t('common.close')}
                />

                {/* Already Registered Modal */}
                <StatusModal
                    isOpen={alreadyRegisteredModalOpen}
                    onClose={() => setAlreadyRegisteredModalOpen(false)}
                    type="info"
                    visitorId={userState.visitorId}
                    showVisitorId={true}
                    title={t('registration.alreadyRegistered')}
                    message={t('registration.alreadyRegisteredMessage')}
                    actionButtonText={t('common.continue')}
                    onAction={async () => {
                        setAlreadyRegisteredModalOpen(false);
                        // Fetch visitor data to restore the complete profile
                        if (userState.visitorId) {
                            console.log('🔄 Fetching visitor data before proceeding...', { visitorId: userState.visitorId });
                            
                            try {
                                const result = await dispatch(fetchVisitorData(userState.visitorId));
                                console.log('📦 Fetch visitor data result:', result);
                                
                                if (fetchVisitorData.fulfilled.match(result)) {
                                    console.log('✅ Visitor data fetched successfully:', result.payload);
                                    console.log('🏪 Current Redux state after fetch:', {
                                        name: userState.name,
                                        email: userState.email,
                                        ageGroup: userState.ageGroup,
                                        nationality: userState.nationality,
                                        hasCompletedRegistration: userState.hasCompletedRegistration
                                    });
                                    
                                    // Small delay to ensure state is updated
                                    setTimeout(() => {
                                        console.log('🧭 Navigating to selfie page...');
                                        // Re-check the current state before navigation
                                        const currentState = store.getState().user;
                                        console.log('🔍 Final state check before navigation:', {
                                            visitorId: currentState.visitorId,
                                            name: currentState.name,
                                            email: currentState.email,
                                            hasCompletedRegistration: currentState.hasCompletedRegistration
                                        });
                                        navigate('/selfie');
                                    }, 100);
                                } else {
                                    console.error('❌ Failed to fetch visitor data:', result);
                                    if (result.payload) {
                                        console.error('Error details:', result.payload);
                                    }
                                    // Still allow user to continue
                                    navigate('/selfie');
                                }
                            } catch (error) {
                                console.error('💥 Unexpected error during fetch:', error);
                                navigate('/selfie');
                            }
                        } else {
                            console.warn('⚠️ No visitorId available, navigating to selfie');
                            navigate('/selfie');
                        }
                    }}
                />
                
                <form onSubmit={handleSubmit} className={`register-form flex flex-col gap-3 ${userState.isLoading ? 'pointer-events-none' : ''}`}>
                    <input 
                        type="text" 
                        name="name" 
                        placeholder={t('registration.namePlaceholder')} 
                        value={form.name}
                        className={`input ${userState.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onChange={handleChange}
                        disabled={userState.isLoading}
                    />
                    
                    <input 
                        type="email" 
                        name="email" 
                        placeholder={t('registration.emailPlaceholder')} 
                        value={form.email}
                        className={`input ${userState.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onChange={handleChange}
                        disabled={userState.isLoading}
                    />
                    
                    {/* Age group selection button */}
                    <button
                        type="button"
                        className={`input flex items-center justify-center cursor-pointer ${
                            form.ageGroup ? 'text-white' : 'text-gray-400'
                        } ${userState.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !userState.isLoading && setAgeGroupModalOpen(true)}
                        disabled={userState.isLoading}
                    >
                        <span className="flex-grow pl-7">
                            {form.ageGroup ? getAgeGroupLabel(form.ageGroup as AgeGroupValue) : t('registration.agePlaceholder')}
                        </span>
                        <ChevronDown className="ml-2" size={20} />
                    </button>
                    
                    {/* Nationality selection button */}
                    <button
                        type="button"
                        className={`input flex items-center justify-center cursor-pointer ${
                            form.nationality ? 'text-white' : 'text-gray-400'
                        } ${userState.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !userState.isLoading && setModalOpen(true)}
                        disabled={userState.isLoading}
                    >
                        <span className="flex-grow pl-7">
                            {form.nationality ? getCountryDisplay(form.nationality) : t('registration.nationalityPlaceholder')}
                        </span>
                        <ChevronDown className="ml-2" size={20} />
                    </button>
                    
                    {/* Age Group Modal */}
                    <AgeGroupModal
                        isOpen={ageGroupModalOpen}
                        onClose={() => setAgeGroupModalOpen(false)}
                        selectedAgeGroup={form.ageGroup}
                        onSelect={handleAgeGroupSelect}
                        disabled={userState.isLoading}
                    />
                    
                    {/* Nationality Modal */}
                    <NationalityModal
                        isOpen={modalOpen}
                        onClose={() => setModalOpen(false)}
                        selectedNationality={form.nationality}
                        onSelect={handleNationalitySelect}
                        disabled={userState.isLoading}
                    />
                    
                    {(form.email.trim() !== '' && !isEmailValid) && <div className="form-error-message">
                        {form.email.trim() !== '' && t('registration.emailError')}
                    </div>}
                    
                    {/* Privacy Policy Acceptance */}
                    <div className="flex items-start gap-3 py-3 pb-4">
                        <button
                            type="button"
                            onClick={handlePrivacyToggle}
                            disabled={userState.isLoading}
                            className={`mt-1 p-0 bg-transparent border-none transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent rounded ${
                                userState.isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:text-gray-300'
                            }`}
                            role="checkbox"
                            aria-checked={userState.privacyAccepted ? "true" : "false"}
                            aria-label={userState.privacyAccepted ? 'Uncheck privacy policy agreement' : 'Check privacy policy agreement'}
                        >
                            {userState.privacyAccepted ? (
                                <CheckSquare 
                                    size={18} 
                                    className="text-white" 
                                />
                            ) : (
                                <Square 
                                    size={18} 
                                    className="text-gray-500" 
                                />
                            )}
                        </button>
                        <div 
                            className={`text-sm text-white leading-relaxed ${
                                userState.isLoading ? 'opacity-50' : 'cursor-pointer'
                            }`}
                            onClick={handlePrivacyToggle}
                        >
                            {t('registration.privacyAcceptanceText')}{' '}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/privacy');
                                }}
                                className="text-white underline hover:text-gray-200 transition-colors"
                                disabled={userState.isLoading}
                            >
                                {t('registration.privacyPolicyLink')}
                            </button>{' '}
                            {t('registration.andTermsOfService')}
                        </div>
                    </div>
                    
                    <div className={`button-wrapper mt-3 stick-to-bottom p-4 !bottom-[4vw] !max-w-[92vw] has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-with-border'
                    }`}>
                        <TcButton 
                            handleClick={() => {
                                if (userState.privacyAccepted && isFormValid && !userState.isLoading) {
                                    const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
                                    handleSubmit(fakeEvent);
                                }
                            }}
                            disabled={!userState.privacyAccepted || !isFormValid || userState.isLoading}
                            primary={userState.privacyAccepted && isFormValid && !userState.isLoading}
                        >
                            {userState.isLoading ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    {t('registration.onboarding')}
                                </div>
                            ) : (
                                t('registration.completeRegistration')
                            )}
                        </TcButton>
                        
                        <div className="mt-4">
                            <button 
                                onClick={userState.isLoading ? undefined : skipRegistration}
                                className={`text-white py-2 text-base transition-opacity ${
                                    userState.isLoading 
                                        ? 'opacity-30 cursor-not-allowed' 
                                        : 'hover:text-gray-200 opacity-100'
                                }`}
                                disabled={userState.isLoading}
                                type="button"
                            >
                                {t('common.skip')}
                            </button>
                        </div>
                    </div>
                </form>
            </TcPaper>
        </div>
    );
}
